import { FormContent } from "@/components/page-canvas";
import { useEffect, useRef, useState } from "react";
import type { FormComponentRef } from "@inertiajs/core";
import { Form, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import { FormErrors } from "@/components/forms/form-errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CatalogueCredit,
  type VehicleCatalogue,
} from "@/pages/vehicles/catalogue-fields";
import VehicleFields, {
  vehicleErrorTargets,
  type VehicleValues,
} from "@/components/forms/vehicle-fields";
import VehiclePhotos from "@/components/forms/vehicle-photos";

type Vehicle = {
  id: string;
  revision: string;
  make: string;
  model: string;
  registration_number?: string;
};
export type CustomerChoice = {
  id: string;
  revision: string;
  name: string;
  vehicles: Vehicle[];
};
export type Draft = Partial<
  Record<
    | "name"
    | "email"
    | "phone"
    | "make"
    | "model"
    | "fabrication_year"
    | "colour"
    | "registration_number"
    | "vin"
    | "service_details"
    | "body"
    | "offering_id"
    | "desired_start"
    | "desired_end"
    | "start_offset"
    | "end_offset"
    | "customer_id"
    | "vehicle_id",
    string
  >
> & {
  time_zone?: string;
  verification_sent: boolean;
  submitted: boolean;
  photos: string[];
  start_offsets?: string[];
  end_offsets?: string[];
  step?: number;
  id?: string;
  restored?: boolean;
  saved_at?: string | null;
  save_token?: string;
  expired?: boolean;
};
export default function EnquiryForm({
  draft,
  customers,
  catalogue,
  action,
  draftPath,
  offerings,
}: {
  draft: Draft;
  customers: CustomerChoice[];
  catalogue: VehicleCatalogue;
  action: string;
  draftPath?: string;
  offerings: { id: string; name: string }[];
}) {
  const { t } = useTranslation();
  const { errors, i18n } = usePage<{ errors: Record<string, string> }>().props;
  const form = useRef<FormComponentRef>(null);
  const [step, setStep] = useState(
    draft.verification_sent ? 2 : draft.step || 0,
  );
  const currentStep = useRef(step);
  const [saveState, setSaveState] = useState<
    "idle" | "saving" | "saved" | "failed"
  >(draft.restored ? "saved" : "idle");
  const [verificationSent, setVerificationSent] = useState(
    draft.verification_sent,
  );
  const [proofUnavailable, setProofUnavailable] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveChain = useRef<Promise<void>>(Promise.resolve());
  const generation = useRef(0);
  const paused = useRef(false);
  const submitPhase = useRef<"idle" | "waiting" | "ready" | "submitting">(
    "idle",
  );
  const [submitPending, setSubmitPending] = useState(false);
  const [photos, setPhotos] = useState(draft.photos);
  const [vehicleFiles, setVehicleFiles] = useState<File[]>([]);
  const [vehicleValues, setVehicleValues] = useState<VehicleValues>({
    make: draft.make || "",
    model: draft.model || "",
    fabrication_year: draft.fabrication_year || "",
    colour: draft.colour || "",
    registration_number: draft.registration_number || "",
    vin: draft.vin || "",
  });
  const savedPhotoCount = useRef(draft.photos.length);
  const [review, setReview] = useState<Record<string, string>>({});
  const stageKeys = ["contact_vehicle", "service_time", "review_verify"];
  useEffect(() => {
    setVerificationSent(draft.verification_sent);
    const uploadsSaved = draft.photos.length > savedPhotoCount.current;
    if (draft.verification_sent || uploadsSaved) {
      setPhotos(draft.photos);
      setVehicleFiles([]);
      const upload = document.getElementById(
        "intake-photos",
      ) as HTMLInputElement | null;
      if (upload) upload.value = "";
    }
    savedPhotoCount.current = draft.photos.length;
  }, [draft.verification_sent, draft.photos]);
  useEffect(() => {
    const navigation = document.getElementById("enquiry-progress");
    const active = navigation?.querySelector<HTMLElement>(
      '[aria-current="step"]',
    );
    if (navigation && active)
      navigation.scrollLeft = Math.max(
        0,
        active.offsetLeft - navigation.offsetLeft - 8,
      );
  }, [step]);
  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      paused.current = true;
    },
    [],
  );
  function saveDraft(nextStep = currentStep.current) {
    if (!draftPath || !draft.save_token || !form.current || paused.current)
      return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    const values = Object.fromEntries(
      Array.from(form.current.getFormData().entries()).flatMap(
        ([name, value]) => {
          const field = name.match(/^website_request\[([a-z_]+)\]$/)?.[1];
          return field && typeof value === "string" ? [[field, value]] : [];
        },
      ),
    );
    const current = ++generation.current;
    setSaveState("saving");
    saveChain.current = saveChain.current.then(async () => {
      if (current !== generation.current) return;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(draftPath, {
          method: "PATCH",
          credentials: "same-origin",
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token":
              document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
                ?.content || "",
          },
          body: JSON.stringify({
            website_request: values,
            step: nextStep,
            draft_token: draft.save_token,
          }),
        });
        if (!response.ok) throw new Error("Draft save failed");
        const result = await response.json();
        if (current === generation.current) {
          setSaveState("saved");
          const upload = document.getElementById(
            "intake-photos",
          ) as HTMLInputElement | null;
          setVerificationSent(
            result.verification_sent === true && !upload?.files?.length,
          );
        }
      } catch {
        if (current === generation.current) setSaveState("failed");
      } finally {
        clearTimeout(timeout);
      }
    });
  }
  function queueSave(nextStep = currentStep.current) {
    if (!draftPath || paused.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    generation.current += 1;
    setSaveState("saving");
    setVerificationSent(false);
    saveTimer.current = setTimeout(() => saveDraft(nextStep), 650);
  }
  function moveTo(nextStep: number) {
    currentStep.current = nextStep;
    setStep(nextStep);
    queueSave(nextStep);
  }
  function next() {
    const fields = document.querySelectorAll<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >(
      `[data-enquiry-step="${step}"] input, [data-enquiry-step="${step}"] textarea, [data-enquiry-step="${step}"] select`,
    );
    for (const field of fields) if (!field.reportValidity()) return;
    const data = form.current?.getFormData();
    if (data) {
      setReview(
        Object.fromEntries(
          Array.from(data.entries()).filter(
            (entry): entry is [string, string] => typeof entry[1] === "string",
          ),
        ),
      );
      setPhotos([
        ...draft.photos,
        ...data
          .getAll("website_request[photos][]")
          .filter((item): item is File => item instanceof File && !!item.name)
          .map((item) => item.name),
      ]);
    }
    moveTo(Math.min(step + 1, 2));
    document.getElementById("enquiry-progress")?.focus();
  }
  const [customerId, setCustomerId] = useState(draft.customer_id || "new");
  const [offeringId, setOfferingId] = useState(draft.offering_id || "");
  const [vehicleId, setVehicleId] = useState(draft.vehicle_id || "new");
  const customer = customers.find((choice) => choice.id === customerId);
  const vehicle = customer?.vehicles.find((choice) => choice.id === vehicleId);
  function choice(
    id: string,
    name: string,
    label: string,
    value: string,
    options: { id: string; name: string }[],
    onChange?: (value: string) => void,
  ) {
    return (
      <div key={id} className="space-y-2">
        <Label htmlFor={id}>{label}</Label>
        <Select
          name={name}
          defaultValue={value}
          items={Object.fromEntries(
            options.map((item) => [item.id, item.name]),
          )}
          onValueChange={(next) => {
            onChange?.(next || "new");
            queueSave();
          }}
        >
          <SelectTrigger id={id} className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (draft.submitted)
    return (
      <FormContent className="space-y-4">
        <p role="status">{t("website_requests.submitted")}</p>
        <p className="text-muted-foreground">
          {t("enquiry_draft.submitted_help")}
        </p>
        <Button render={<a href={`${action}?new=true`} />}>
          {t("website_requests.another")}
        </Button>
      </FormContent>
    );
  if (action.startsWith("https://"))
    return (
      <Button render={<a href={action} />}>
        {t("website_requests.continue")}
      </Button>
    );
  return (
    <FormContent>
      <Form
        ref={form}
        action={action}
        method="post"
        encType="multipart/form-data"
        noValidate
        disableWhileProcessing
        onInput={() => queueSave()}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) saveDraft();
        }}
        onBefore={() => {
          if (step < 2) {
            next();
            return false;
          }
          if (
            submitPhase.current === "waiting" ||
            submitPhase.current === "submitting"
          )
            return false;
          if (submitPhase.current === "idle") {
            if (saveTimer.current) clearTimeout(saveTimer.current);
            paused.current = true;
            submitPhase.current = "waiting";
            setSubmitPending(true);
            void saveChain.current.then(() => {
              submitPhase.current = "ready";
              form.current?.submit();
            });
            return false;
          }
          submitPhase.current = "submitting";
        }}
        onFinish={() => {
          paused.current = false;
          submitPhase.current = "idle";
          setSubmitPending(false);
        }}
        onHttpException={(response) => {
          if (response.status === 404) {
            setProofUnavailable(true);
            return false;
          }
        }}
        onError={(nextErrors) =>
          moveTo(
            Object.keys(nextErrors).some((key) =>
              /name|email|phone|vehicle|make|model|fabrication_year|colour|registration_number|vin|photos|files/.test(
                key,
              ),
            )
              ? 0
              : 1,
          )
        }
      >
        {({ processing }) => (
          <div className="space-y-7">
            <input
              type="hidden"
              name="draft_token"
              value={draft.save_token || ""}
            />
            <div className="space-y-2 border-b pb-5">
              <p className="text-sm text-muted-foreground">
                {t("enquiry_draft.private_help")}
              </p>
              {draft.expired && (
                <p role="alert" className="text-sm">
                  {t("enquiry_draft.expired")}
                </p>
              )}
              {proofUnavailable && (
                <div role="alert" className="space-y-3 rounded-lg border p-4">
                  <p>{t("enquiry_draft.proof_unavailable")}</p>
                  <Button variant="outline" render={<a href={action} />}>
                    {t("enquiry_draft.open_current")}
                  </Button>
                </div>
              )}
              {draftPath && (
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <p role="status" aria-live="polite">
                    {t(`enquiry_draft.${saveState}`)}
                  </p>
                  {saveState === "failed" && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => saveDraft()}
                    >
                      {t("enquiry_draft.retry")}
                    </Button>
                  )}
                </div>
              )}
            </div>
            <nav
              id="enquiry-progress"
              tabIndex={-1}
              aria-label={t("access.setup_steps")}
              className="flex min-w-0 gap-2 overflow-x-auto pb-2 outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {stageKeys.map((key, index) => (
                <Button
                  key={key}
                  type="button"
                  className="shrink-0"
                  variant={step === index ? "default" : "outline"}
                  disabled={index > step}
                  aria-current={step === index ? "step" : undefined}
                  onClick={() => moveTo(index)}
                >
                  {index + 1}. {t(`access.${key}`)}
                </Button>
              ))}
            </nav>
            <FormErrors
              key={step}
              errors={errors || {}}
              targets={{
                ...Object.fromEntries(
                  [
                    "name",
                    "email",
                    "phone",
                    "service_details",
                    "body",
                    "desired_start",
                    "desired_end",
                  ].map((key) => [key, `intake-${key}`]),
                ),
                ...vehicleErrorTargets("vehicle"),
                photos: "intake-photos",
                files: "intake-photos",
              }}
            />
            <p className="text-sm text-muted-foreground">
              {[
                offerings.find((offering) => offering.id === offeringId)?.name,
                draft.time_zone || i18n.time_zone,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
            {verificationSent && (
              <p role="status" className="rounded-lg border p-4">
                {t("website_requests.verification_sent")}
              </p>
            )}
            <div
              hidden={step !== 0}
              data-enquiry-step="0"
              className="space-y-7"
            >
              <fieldset className="space-y-4">
                <legend className="mb-3 text-lg font-semibold">
                  {t("website_requests.contact")}
                </legend>
                {customers.length > 0 &&
                  choice(
                    "intake-customer",
                    "website_request[customer_id]",
                    t("website_requests.customer"),
                    customerId,
                    [
                      { id: "new", name: t("website_requests.new_details") },
                      ...customers,
                    ],
                    (value) => {
                      setCustomerId(value);
                      setVehicleId("new");
                    },
                  )}
                <input
                  type="hidden"
                  name="website_request[customer_revision]"
                  value={customer?.revision || ""}
                />
                {(["name", "email", "phone"] as const).map((field) => (
                  <div className="space-y-2" key={`${customerId}-${field}`}>
                    <Label htmlFor={`intake-${field}`}>
                      {t(`website_requests.${field}`)}
                      {field !== "phone" ? " *" : ""}
                    </Label>
                    <Input
                      id={`intake-${field}`}
                      name={`website_request[${field}]`}
                      type={
                        field === "email"
                          ? "email"
                          : field === "phone"
                            ? "tel"
                            : "text"
                      }
                      defaultValue={
                        field === "name" && customer
                          ? customer.name
                          : draft[field] || ""
                      }
                      autoComplete={field === "phone" ? "tel" : field}
                      required={field !== "phone"}
                      maxLength={field === "name" ? 200 : 255}
                      aria-invalid={!!errors[field]}
                    />
                  </div>
                ))}
                <p className="text-muted-foreground text-sm">
                  {t("website_requests.privacy")}
                </p>
              </fieldset>
              <fieldset className="space-y-4">
                <legend className="mb-3 text-lg font-semibold">
                  {t("vehicles.details")}
                </legend>
                {customer &&
                  choice(
                    `intake-vehicle-${customerId}`,
                    "website_request[vehicle_id]",
                    t("website_requests.vehicle"),
                    vehicleId,
                    [
                      { id: "new", name: t("website_requests.new_vehicle") },
                      ...customer.vehicles.map((item) => ({
                        id: item.id,
                        name: `${item.make} ${item.model} ${item.registration_number || ""}`,
                      })),
                    ],
                    setVehicleId,
                  )}
                <input
                  type="hidden"
                  name="website_request[vehicle_revision]"
                  value={vehicle?.revision || ""}
                />
                {vehicle ? (
                  <p>
                    {vehicle.make} {vehicle.model} {vehicle.registration_number}
                  </p>
                ) : (
                  <>
                    <VehicleFields
                      values={vehicleValues}
                      catalogue={catalogue}
                      errors={errors}
                      inputPrefix="website_request"
                      idPrefix="vehicle"
                      autoFocus={false}
                      onChange={(field, value) => {
                        setVehicleValues((current) => ({
                          ...current,
                          [field]: value,
                        }));
                        queueSave();
                      }}
                    />
                    <CatalogueCredit />
                  </>
                )}
                <VehiclePhotos
                  name="website_request[photos][]"
                  id="intake-photos"
                  files={vehicleFiles}
                  onChange={(files) => {
                    setVehicleFiles(files);
                    queueSave();
                  }}
                  existingPhotos={draft.photos.map((name, index) => ({
                    id: String(index),
                    name,
                  }))}
                  error={errors.photos || errors.files}
                  help={t("enquiry_draft.photos_help")}
                />
              </fieldset>
            </div>
            <div hidden={step !== 1} data-enquiry-step="1">
              <fieldset className="space-y-4">
                <legend className="mb-3 text-lg font-semibold">
                  {t("website_requests.service")}
                </legend>
                {offerings.length > 0 &&
                  choice(
                    "intake-offering",
                    "website_request[offering_id]",
                    t("website.services"),
                    offeringId,
                    [
                      { id: "", name: t("website_requests.describe_service") },
                      ...offerings,
                    ],
                    setOfferingId,
                  )}
                {(["service_details", "body"] as const).map((field) => (
                  <div key={field} className="space-y-2">
                    <Label htmlFor={`intake-${field}`}>
                      {t(`website_requests.${field}`)} *
                    </Label>
                    <Textarea
                      id={`intake-${field}`}
                      name={`website_request[${field}]`}
                      defaultValue={draft[field] || ""}
                      required
                      maxLength={10000}
                      aria-invalid={!!errors[field]}
                    />
                  </div>
                ))}
                <p className="text-muted-foreground text-sm">
                  {t("website_requests.interval_help", {
                    zone: draft.time_zone || i18n.time_zone,
                  })}
                </p>
                <div className="form-fields">
                  {(["desired_start", "desired_end"] as const).map((field) => (
                    <div className="space-y-2" key={field}>
                      <Label htmlFor={`intake-${field}`}>
                        {t(`website_requests.${field}`)} *
                      </Label>
                      <Input
                        id={`intake-${field}`}
                        name={`website_request[${field}]`}
                        type="datetime-local"
                        required
                        defaultValue={draft[field] || ""}
                        aria-invalid={!!errors[field]}
                      />
                    </div>
                  ))}
                </div>
                {(["start", "end"] as const).map(
                  (side) =>
                    (draft[`${side}_offsets`]?.length || 0) > 0 && (
                      <div key={side}>
                        {choice(
                          `intake-${side}-offset`,
                          `website_request[${side}_offset]`,
                          t(`website_requests.${side}_offset`),
                          draft[`${side}_offset`] || "",
                          [
                            {
                              id: "",
                              name: t("website_requests.choose_offset"),
                            },
                            ...(draft[`${side}_offsets`] || []).map(
                              (offset) => ({
                                id: offset,
                                name: `UTC${offset}`,
                              }),
                            ),
                          ],
                        )}
                      </div>
                    ),
                )}
              </fieldset>
            </div>
            {step === 2 && (
              <section
                className="space-y-4 rounded-lg border p-4"
                aria-label={t("access.review_verify")}
              >
                <h2 className="font-semibold">{t("access.review_verify")}</h2>
                <p className="text-sm text-muted-foreground">
                  {t("access.enquiry_review_help")}
                </p>
                <dl className="form-fields">
                  {[
                    "name",
                    "email",
                    "phone",
                    "make",
                    "model",
                    "registration_number",
                    "service_details",
                    "body",
                    "desired_start",
                    "desired_end",
                  ].map((field) => (
                    <div key={field}>
                      <dt className="text-sm text-muted-foreground">
                        {t(
                          `${["make", "model", "registration_number"].includes(field) ? "vehicles" : "website_requests"}.${field}`,
                        )}
                      </dt>
                      <dd className="break-words whitespace-pre-wrap">
                        {review[`website_request[${field}]`] ||
                          (vehicle &&
                          ["make", "model", "registration_number"].includes(
                            field,
                          )
                            ? vehicle[field as keyof Vehicle]
                            : undefined) ||
                          (draft[field as keyof Draft] as string) ||
                          "—"}
                      </dd>
                    </div>
                  ))}
                </dl>
                <p className="font-medium">
                  {offerings.find((offering) => offering.id === offeringId)
                    ?.name || t("website_requests.describe_service")}
                </p>
                <p className="text-sm">{draft.time_zone || i18n.time_zone}</p>
                {photos.length > 0 && (
                  <ul className="list-inside list-disc break-words">
                    {photos.map((photo, index) => (
                      <li key={index}>{photo}</li>
                    ))}
                  </ul>
                )}
              </section>
            )}
            <div className="sticky bottom-0 flex flex-wrap gap-3 border-t bg-canvas py-4">
              {step > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => moveTo(step - 1)}
                >
                  {t("common.back")}
                </Button>
              )}
              {step < 2 ? (
                <Button
                  key="enquiry-continue"
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    next();
                  }}
                >
                  {t("common.continue")}
                </Button>
              ) : (
                <Button
                  key="enquiry-submit"
                  type="submit"
                  disabled={processing || submitPending || proofUnavailable}
                  aria-busy={processing || submitPending}
                >
                  {t(
                    verificationSent
                      ? "website_requests.resend"
                      : "website_requests.submit",
                  )}
                </Button>
              )}
            </div>
          </div>
        )}
      </Form>
    </FormContent>
  );
}
